import React, { createContext, useContext, useEffect, useState } from 'react';
import { useWeb3React } from '@web3-react/core';

import { useEagerConnect } from 'hooks';
import { getChainData } from 'utils/chain';
import { injected } from 'utils/connectors';
import { DEFAULT_CHAIN } from 'utils/constants';
import { ChainData, ChainId } from 'utils/types';
import { CacheKey, removeFromLocalStorage } from 'utils/localStorage';

type ContextProps = Partial<ChainData> & {
  isInitialized: boolean;
  account: string | null | undefined;
  connect: () => void;
  disconnect: () => void;
  setChainId: (chainId: ChainId) => void;
};

declare global {
  interface Window {
    ethereum?: any;
  }
}

const ChainContext = createContext({} as ContextProps);

export const useChain = () => useContext(ChainContext);

export const ChainProvider: React.FC = (props) => {
  const [isInitialized, setInitialized] = useState(false);
  const { chainId: web3ChainId, account, activate, deactivate } = useWeb3React();
  const [chainId, setChainId] = useState<ChainId | undefined>(web3ChainId);
  useEagerConnect();

  // Update chainId with whatever the provider is set to
  useEffect(() => {
    setChainId(web3ChainId);
  }, [web3ChainId]);

  useEffect(() => {
    // we already have a chainId set
    if (chainId) {
      return;
    }

    // browser is not web3-compatible, just set default chain
    if (typeof window.ethereum === 'undefined') {
      return setChainId(DEFAULT_CHAIN);
    }

    // web3 chain is already set
    if (window.ethereum.chainId) {
      return setChainId(Number(window.ethereum.chainId));
    }

    // fall back to default chain if nothing is found in the given timeout period
    const loadingTimeout = setTimeout(() => {
      setChainId(DEFAULT_CHAIN);
    }, 750);
    return () => clearTimeout(loadingTimeout);
  }, [chainId]);

  // add a network/chain change listener
  useEffect(() => {
    if (window.ethereum?.on) {
      window.ethereum.on('networkChanged', setChainId);
    }
  }, []);

  // show loading screen if no chain has been set yet
  useEffect(() => {
    setInitialized(!!chainId);
  }, [chainId]);

  return (
    <ChainContext.Provider
      {...props}
      value={{
        isInitialized,
        ...(chainId ? getChainData(chainId) : {}),
        account,
        connect: () => activate(injected),
        disconnect: () => {
          removeFromLocalStorage(CacheKey.Accounts);
          deactivate();
        },
        setChainId,
      }}
    />
  );
};
